import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

/**
 * MultiSelectChips - shows currently-selected values as chips and lets the admin
 *  - remove any chip with the X button
 *  - add a new free-form value via the + Add Custom input
 *  - quick-toggle from a list of `presets` (chips are highlighted when selected)
 *
 * Props:
 *  - label: section label
 *  - value: array of strings/numbers
 *  - onChange: (newArray) => void
 *  - presets: optional array of suggested values that show as click-to-toggle chips
 *  - kind: "string" | "number" - convert input to number when "number"
 *  - placeholder: input placeholder
 *  - testId: data-testid prefix
 */
export const MultiSelectChips = ({
  label,
  value = [],
  onChange,
  presets = [],
  kind = "string",
  placeholder = "Add new option…",
  testId = "chips",
}) => {
  const [draft, setDraft] = useState("");
  const arr = Array.isArray(value) ? value : [];

  const isSelected = (v) => arr.some((x) => String(x) === String(v));

  const togglePreset = (v) => {
    if (isSelected(v)) {
      onChange(arr.filter((x) => String(x) !== String(v)));
    } else {
      onChange([...arr, kind === "number" ? Number(v) : v]);
    }
  };

  const removeChip = (v) => onChange(arr.filter((x) => String(x) !== String(v)));

  const addDraft = () => {
    const t = draft.trim();
    if (!t) return;
    const v = kind === "number" ? Number(t) : t;
    if (kind === "number" && Number.isNaN(v)) return;
    if (!arr.some((x) => String(x) === String(v))) onChange([...arr, v]);
    setDraft("");
  };

  return (
    <div className="py-2" data-testid={`${testId}-section`}>
      {label && (
        <p className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
          {label}
        </p>
      )}

      {/* Selected chips */}
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[24px]">
        {arr.length === 0 && (
          <span className="text-[10px] text-slate-400 italic">
            No options selected
          </span>
        )}
        {arr.map((v) => (
          <Badge
            key={String(v)}
            className="bg-[#8B1A1A] hover:bg-[#6B1515] text-white text-[10px] gap-1 pr-1 pl-2"
            data-testid={`${testId}-chip-${String(v).replace(/\s+/g, "_")}`}
          >
            {String(v)}
            <button
              type="button"
              onClick={() => removeChip(v)}
              className="hover:bg-white/20 rounded-full p-0.5"
              aria-label={`Remove ${v}`}
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </Badge>
        ))}
      </div>

      {/* Preset suggestions (toggle on/off) */}
      {presets.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {presets.map((p) => {
            const sel = isSelected(p);
            return (
              <button
                key={String(p)}
                type="button"
                onClick={() => togglePreset(p)}
                className={`px-2 py-0.5 rounded-full text-[10px] border transition ${
                  sel
                    ? "bg-emerald-50 dark:bg-emerald-900/30 border-emerald-400 text-emerald-700 dark:text-emerald-300"
                    : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 hover:border-slate-400"
                }`}
                data-testid={`${testId}-preset-${String(p).replace(/\s+/g, "_")}`}
              >
                {sel ? "✓ " : "+ "} {String(p)}
              </button>
            );
          })}
        </div>
      )}

      {/* Add custom input + button */}
      <div className="flex items-center gap-1.5">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addDraft();
            }
          }}
          placeholder={placeholder}
          className="h-7 text-xs"
          data-testid={`${testId}-input`}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={addDraft}
          className="h-7 px-2 shrink-0 border-[#8B1A1A] text-[#8B1A1A] hover:bg-[#8B1A1A] hover:text-white"
          data-testid={`${testId}-add-btn`}
        >
          <Plus className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
};

export default MultiSelectChips;
